import { useEffect, useState } from "react";

// Imperative singleton for showing a conflict dialog from outside React
// (we're called from storage.ts which is not a React component). The
// dialog is mounted once at App-level and listens for `pending` items.

type Choice = "reload" | "overwrite";
type Pending = { key: string; message: string; resolve: (c: Choice) => void };

let setPendingExternal: ((p: Pending | null) => void) | null = null;
let queued: Pending | null = null;

export function showConflictDialog(key: string, message: string): Promise<Choice> {
  return new Promise((resolve) => {
    const item: Pending = { key, message, resolve };
    if (setPendingExternal) {
      setPendingExternal(item);
    } else {
      queued = item;
    }
  });
}

export function ConflictDialog() {
  const [pending, setPending] = useState<Pending | null>(queued);

  useEffect(() => {
    setPendingExternal = setPending;
    if (queued) {
      setPending(queued);
      queued = null;
    }
    return () => {
      setPendingExternal = null;
    };
  }, []);

  if (!pending) return null;

  const choose = (c: Choice) => {
    pending.resolve(c);
    setPending(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold text-slate-900">
          Iemand anders heeft ook bewerkt
        </h2>
        <p className="mb-4 text-sm text-slate-600">{pending.message}</p>
        <p className="mb-5 text-sm text-slate-600">
          Wat wil je doen?
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => choose("reload")}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
          >
            Server-versie laden
          </button>
          <button
            onClick={() => choose("overwrite")}
            className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            Mijn versie opslaan
          </button>
        </div>
      </div>
    </div>
  );
}
